import { useState, useEffect } from 'react'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Checkbox } from '../ui/checkbox'
import { RecipientSelector, RecipientType as UIRecipientType } from './RecipientSelector'
import {
  NotificationType,
  NotificationChannel,
  NotificationPriority,
  CreateNotificationDto,
  RecipientType as ApiRecipientType,
  getNotificationTypeLabel,
  getNotificationPriorityLabel,
} from '@tco/shared-types'

interface NotificationFormProps {
  onSubmit: (data: CreateNotificationDto, sendImmediately: boolean) => void
  isLoading?: boolean
  initialData?: Partial<CreateNotificationDto>
  mode?: 'create' | 'edit'
}

export function NotificationForm({
  onSubmit,
  isLoading = false,
  initialData,
  mode = 'create',
}: NotificationFormProps) {
  // Form state
  const [type, setType] = useState<NotificationType>(
    initialData?.type || NotificationType.GENERAL
  )
  const [title, setTitle] = useState(initialData?.title || '')
  const [body, setBody] = useState(initialData?.body || '')
  const [priority, setPriority] = useState<NotificationPriority>(
    initialData?.priority || NotificationPriority.NORMAL
  )
  const [channels, setChannels] = useState<NotificationChannel[]>(
    initialData?.channels || [NotificationChannel.PUSH]
  )

  // Recipient state (using UI RecipientType)
  const [recipientType, setRecipientType] = useState<UIRecipientType>(() => {
    if (initialData?.is_broadcast) return 'broadcast'
    if (initialData?.recipient_employee_id) return 'employee'
    if (initialData?.recipient_role_id) return 'role'
    if (initialData?.recipient_department_id) return 'department'
    return 'employee'
  })
  const [recipientId, setRecipientId] = useState(() => {
    return (
      initialData?.recipient_employee_id ||
      initialData?.recipient_role_id ||
      initialData?.recipient_department_id ||
      ''
    )
  })

  // Scheduling state
  const [scheduledFor, setScheduledFor] = useState(initialData?.scheduled_for || '')
  const [sendImmediately, setSendImmediately] = useState(true)

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Channel toggle handlers
  const toggleChannel = (channel: NotificationChannel) => {
    setChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel]
    )
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!title.trim()) {
      newErrors.title = 'Заголовок обязателен'
    }

    if (!body.trim()) {
      newErrors.body = 'Текст уведомления обязателен'
    }

    if (channels.length === 0) {
      newErrors.channels = 'Выберите хотя бы один канал доставки'
    }

    if (recipientType !== 'broadcast' && !recipientId) {
      newErrors.recipient = 'Выберите получателя'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    // Map frontend UI recipient types to backend API RecipientType enum
    const mapRecipientType = (frontendType: UIRecipientType): ApiRecipientType => {
      switch (frontendType) {
        case 'broadcast':
          return ApiRecipientType.ALL
        case 'department':
          return ApiRecipientType.GROUP
        default: // 'employee', 'role'
          return ApiRecipientType.SINGLE
      }
    }

    const data: CreateNotificationDto = {
      type,
      title: title.trim(),
      message: body.trim(), // Changed from 'body' to 'message'
      recipient_type: mapRecipientType(recipientType),
    }

    // Add recipient info based on type
    if (recipientType === 'employee' || recipientType === 'role') {
      data.recipient_ids = [recipientId] // Backend expects array
    } else if (recipientType === 'department') {
      data.department_id = recipientId
    }
    // For broadcast (ALL), no additional fields needed

    // Add scheduling if needed
    if (!sendImmediately && scheduledFor) {
      data.scheduled_at = scheduledFor // Changed from 'scheduled_for'
    }

    onSubmit(data, sendImmediately)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Notification Type */}
      <div className="space-y-2">
        <Label htmlFor="type">Тип уведомления</Label>
        <Select value={type} onValueChange={(value) => setType(value as NotificationType)}>
          <SelectTrigger id="type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(NotificationType).map((notifType) => (
              <SelectItem key={notifType} value={notifType}>
                {getNotificationTypeLabel(notifType)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">
          Заголовок <span className="text-destructive">*</span>
        </Label>
        <Input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Краткий заголовок уведомления"
          disabled={isLoading}
          className={errors.title ? 'border-destructive' : ''}
        />
        {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
      </div>

      {/* Body */}
      <div className="space-y-2">
        <Label htmlFor="body">
          Текст уведомления <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Полный текст уведомления..."
          rows={4}
          disabled={isLoading}
          className={errors.body ? 'border-destructive' : ''}
        />
        {errors.body && <p className="text-xs text-destructive">{errors.body}</p>}
        <p className="text-xs text-muted-foreground">{body.length} символов</p>
      </div>

      {/* Priority */}
      <div className="space-y-2">
        <Label htmlFor="priority">Приоритет</Label>
        <Select
          value={priority}
          onValueChange={(value) => setPriority(value as NotificationPriority)}
        >
          <SelectTrigger id="priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(NotificationPriority).map((priorityValue) => (
              <SelectItem key={priorityValue} value={priorityValue}>
                {getNotificationPriorityLabel(priorityValue)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Channels */}
      <div className="space-y-2">
        <Label>
          Каналы доставки <span className="text-destructive">*</span>
        </Label>
        <div className="flex flex-col gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="channel-push"
              checked={channels.includes(NotificationChannel.PUSH)}
              onCheckedChange={() => toggleChannel(NotificationChannel.PUSH)}
              disabled={isLoading}
            />
            <Label htmlFor="channel-push" className="font-normal cursor-pointer">
              Push-уведомление
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="channel-email"
              checked={channels.includes(NotificationChannel.EMAIL)}
              onCheckedChange={() => toggleChannel(NotificationChannel.EMAIL)}
              disabled={isLoading}
            />
            <Label htmlFor="channel-email" className="font-normal cursor-pointer">
              Email
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="channel-sms"
              checked={channels.includes(NotificationChannel.SMS)}
              onCheckedChange={() => toggleChannel(NotificationChannel.SMS)}
              disabled={isLoading}
            />
            <Label htmlFor="channel-sms" className="font-normal cursor-pointer">
              SMS
            </Label>
          </div>
        </div>
        {errors.channels && <p className="text-xs text-destructive">{errors.channels}</p>}
      </div>

      {/* Recipient Selector */}
      <RecipientSelector
        recipientType={recipientType}
        recipientId={recipientId}
        onRecipientTypeChange={setRecipientType}
        onRecipientIdChange={setRecipientId}
        disabled={isLoading}
      />
      {errors.recipient && <p className="text-xs text-destructive">{errors.recipient}</p>}

      {/* Scheduling */}
      {mode === 'create' && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-immediately"
              checked={sendImmediately}
              onCheckedChange={(checked) => setSendImmediately(checked === true)}
              disabled={isLoading}
            />
            <Label htmlFor="send-immediately" className="font-normal cursor-pointer">
              Отправить немедленно
            </Label>
          </div>

          {!sendImmediately && (
            <div className="space-y-2 pl-6">
              <Label htmlFor="scheduled-for">Запланировать отправку на</Label>
              <Input
                id="scheduled-for"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Уведомление будет отправлено в указанное время
              </p>
            </div>
          )}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading ? 'Обработка...' : mode === 'create' ? 'Создать уведомление' : 'Сохранить изменения'}
        </Button>
      </div>
    </form>
  )
}
